import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddGradedUnits,
    CreateGradedUnit,
    CreateGradedUnitFailure,
    CreateGradedUnitFromCourseOffering,
    CreateGradedUnitFromCourseOfferingFailure,
    CreateGradedUnitFromCourseOfferingSuccess,
    CreateGradedUnitSuccess,
    DeleteGradedUnit,
    DeleteGradedUnitFailure,
    DeleteGradedUnitSuccess,
    FetchGradedUnit,
    FetchGradedUnitFailure,
    FetchGradedUnitMetrics,
    FetchGradedUnitMetricsFailure,
    FetchGradedUnitMetricsSuccess,
    FetchGradedUnitSuccess,
    SetGradedUnit,
    SetGradedUnits,
    UnsetGradedUnit,
    UpdateGradedUnit,
    UpdateGradedUnitFailure,
    UpdateGradedUnitSuccess,
} from '../actions';
import { ICreateGradedUnitFromCourseOfferingActionPayload } from '../interfaces/create-graded-unit-from-course-offering.action.payload.interface';
import { GradedUnitApiService } from '../services/graded-unit.api-service';
import { GradedUnit } from './graded-unit.model';
import { GradedUnitStateModel } from './graded-unit.state-model';

@State<GradedUnitStateModel>({
    name: 'gradedUnit',
    defaults: new GradedUnitStateModel(),
})
@Injectable()
export class GradedUnitState extends EntityState<GradedUnit> implements IEntityState<GradedUnit> {
    @Selector()
    static getEntity(state: GradedUnitStateModel, id: string = state.item): GradedUnit {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: GradedUnitStateModel): GradedUnit[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(['sequence']));

        return toReturn;
    }

    constructor(private _gradedUnitApiService: GradedUnitApiService) {
        super(_gradedUnitApiService, 30);

        this._propstoSearch = GradedUnit.props_to_search;
    }

    @Action(AddGradedUnits)
    addEntities(ctx: StateContext<GradedUnitStateModel>, action: AddGradedUnits): void {
        this._addEntities(ctx, action);
    }

    @Action(SetGradedUnit)
    setEntity(ctx: StateContext<GradedUnitStateModel>, action: SetGradedUnit): void {
        this._setEntity(ctx, action, AddGradedUnits);
    }

    @Action(UnsetGradedUnit)
    unsetEntity(ctx: StateContext<GradedUnitStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateGradedUnit)
    createEntity(ctx: StateContext<GradedUnitStateModel>, action: CreateGradedUnit): void {
        this._createEntity(ctx, action, CreateGradedUnitSuccess, CreateGradedUnitFailure, AddGradedUnits).then(() => {});
    }

    @Action(UpdateGradedUnit)
    updateEntity(ctx: StateContext<GradedUnitStateModel>, action: UpdateGradedUnit): void {
        this._updateEntity(ctx, action, UpdateGradedUnitSuccess, UpdateGradedUnitFailure, AddGradedUnits).then(() => {});
    }

    @Action(DeleteGradedUnit)
    deleteEntity(ctx: StateContext<GradedUnitStateModel>, action: DeleteGradedUnit): void {
        this._deleteEntity(ctx, action, DeleteGradedUnitSuccess, DeleteGradedUnitFailure).then(() => {});
    }

    @Action(FetchGradedUnit)
    fetchEntity(ctx: StateContext<GradedUnitStateModel>, action: FetchGradedUnit): void {
        this._fetchEntity(ctx, action, FetchGradedUnitSuccess, FetchGradedUnitFailure, AddGradedUnits, SetGradedUnit).then(() => {});
    }

    @Action(FetchGradedUnitSuccess)
    fetchEntitySuccess(ctx: StateContext<GradedUnitStateModel>, action: FetchGradedUnitSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchGradedUnitFailure)
    fetchEntityFailure(ctx: StateContext<GradedUnitStateModel>, action: FetchGradedUnitFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetGradedUnits)
    setEntities(ctx: StateContext<GradedUnitStateModel>, action: SetGradedUnits): void {
        this._setEntities(ctx, action);
    }

    fetchEntities(state: StateContext<GradedUnitStateModel>, SetEntityAction: any, SuccessAction: any, FailureAction: any): void {
        throw new Error('Method not implemented.');
    }

    fetchEntitiesSuccess(state: StateContext<GradedUnitStateModel>): void {
        throw new Error('Method not implemented.');
    }

    fetchEntitiesFailure(state: StateContext<GradedUnitStateModel>): void {
        throw new Error('Method not implemented.');
    }

    @Action(FetchGradedUnitMetrics)
    fetchGradedUnitMetrics({ dispatch, getState, patchState }: StateContext<GradedUnitStateModel>, { payload }: FetchGradedUnitMetrics): void {
        this._gradedUnitApiService.metrics(payload).subscribe(
            (metrics) => {
                const state = getState();
                const map = new Map(state.map);

                map.set(
                    payload,
                    Object.assign(new GradedUnit(), state.map.get(payload), {
                        metrics,
                    })
                );

                patchState({
                    map,
                });

                const actions = [];

                if (state.item && state.item === payload) {
                    const stateItem = map.get(state.item);

                    if (!stateItem) {
                        throw new Error();
                    }

                    actions.push(
                        new SetGradedUnit(
                            Object.assign(new GradedUnit(), stateItem, {
                                metrics,
                            })
                        )
                    );
                }

                actions.push(new FetchGradedUnitMetricsSuccess(metrics));

                dispatch(actions);
            },
            (error: HttpErrorResponse) => {
                dispatch(
                    new FetchGradedUnitMetricsFailure({
                        entityId: payload,
                        response: error,
                    })
                );
            }
        );
    }

    @Action(CreateGradedUnitFromCourseOffering)
    createGradedUnitFromCourseOffering(ctx: StateContext<GradedUnitStateModel>, action: CreateGradedUnitFromCourseOffering): void {
        this._gradedUnitApiService.createGradedUnitFromCourseOffering(action.payload).subscribe(
            (gradedUnit) => {
                ctx.dispatch([new CreateGradedUnitFromCourseOfferingSuccess(gradedUnit)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreateGradedUnitFromCourseOfferingFailure({ response }));
            }
        );
    }
}
